import { useRef, useState } from 'react';

import { STEUERSAETZE, formatiereEuro, type Position, type Steuersatz } from '../warenkorb';
import { MAX_VORGABE_MENGE } from '../schnellwahl';
import {
  eingabeGueltig,
  eingabeStarten,
  eingabeTaste,
  eingabeUebernehmen,
  freiePosition,
  kannErhoehen,
  kannVerringern,
  MENGE_MAX,
  mengeErhoehen,
  mengeVerringern,
  tastaturZuMengenTaste,
  type MengenEingabe,
} from '../mengenfeld';
import { ZIFFERN_TASTEN, tastaturZuZiffer, ziffernTaste, type ZiffernTaste } from '../ziffern';
import {
  Bildschirmtastatur,
  TastaturKnopf,
  istTastaturEsc,
  useBildschirmtastatur,
} from './Bildschirmtastatur';

/**
 * Tastenfeld des Ziffernblocks (7–9, 4–6, 1–3, 00, 0, ⌫) – gemeinsam für die freie Position und
 * den Bar-Dialog V2. Ohne Zusatzangaben entsteht genau das bisherige Markup.
 */
export function ZiffernTasten(props: {
  onTaste: (t: ZiffernTaste) => void;
  /** Alle Tasten gesperrt (z. B. während der Verarbeitung). */
  gesperrt?: boolean;
  /** Zusätzliche Klasse am Tastenfeld. */
  klasse?: string;
  /** Beschriftung der Tastengruppe für Hilfstechnik (setzt role="group"). */
  beschriftung?: string;
  /** Fokus beim Antippen nicht vom Eingabefeld nehmen (Enter bestätigt dann weiter das Feld). */
  fokusHalten?: boolean;
}) {
  const { onTaste, gesperrt, klasse, beschriftung, fokusHalten } = props;
  return (
    <div
      className={klasse === undefined ? 'ziffern-tasten' : `ziffern-tasten ${klasse}`}
      {...(beschriftung === undefined ? {} : { role: 'group', 'aria-label': beschriftung })}
    >
      {ZIFFERN_TASTEN.map((t) => (
        <button
          key={t}
          type="button"
          {...(gesperrt === undefined ? {} : { disabled: gesperrt })}
          {...(fokusHalten === true
            ? {
                onMouseDown: (e: { preventDefault: () => void }) => {
                  e.preventDefault();
                },
              }
            : {})}
          onClick={() => {
            onTaste(t);
          }}
          aria-label={t === '⌫' ? 'Letzte Ziffer löschen' : t}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/**
 * Fenster „Menge oder Betrag“ (POS V2 Phase 2, Rahmen D8 `17:3252`; Mengenfeld nach MB2 v1,
 * Section `388:17315`): Umschalter Betrag / Stückzahl für die nächste Kachel, Anzeige von
 * rechts eingeschoben, Ziffernblock, Steuersatz (nur Betrag), Bezeichnung und rechts daneben
 * das Mengenfeld [−][Wert][+], Abbrechen (Esc) / Hinzufügen (Enter).
 * Physische Tastatur funktioniert ebenso (Ziffern, Rücktaste, Enter, Esc).
 * Eingabe in Cent bleibt beim Verkauf (ein Scan nutzt sie weiterhin als Stückzahl).
 *
 * MB2 Direkteingabe (Regeln M3–M6): Antippen des Werts markiert ihn; Ziffernblock und
 * Hardware-Tastatur tippen dann die Stückzahl (1–999), der Betrag bleibt. Enter bzw.
 * „Übernehmen“ übernimmt, Esc/„Abbrechen“ bzw. Antippen des Betrags kehrt ohne Änderung zurück.
 */
export function Ziffernblock(props: {
  onHinzufuegen: (p: Position) => void;
  /** Eingabe in Cent (von rechts eingeschoben) – vom Verkauf gehalten, damit ein Scan sie als Menge nutzen kann. */
  cent: number;
  setCent: (f: (alt: number) => number) => void;
  menge: number;
  setMenge: (n: number) => void;
  /** Stückzahl für die nächste Kachel bzw. den nächsten Scan vormerken (Modus „Stückzahl“). */
  onVormerken?: (stueck: number) => void;
  /** Fenster schließen ohne Übernahme (Esc / Abbrechen). */
  onAbbrechen?: () => void;
}) {
  const { cent, setCent, menge, setMenge } = props;
  const [steuersatz, setSteuersatz] = useState<Steuersatz>('20');
  const [bezeichnung, setBezeichnung] = useState('');
  const [modus, setModus] = useState<'betrag' | 'stueck'>('betrag');
  const stueck = Number.isInteger(cent) && cent >= 1 && cent <= MAX_VORGABE_MENGE ? cent : null;
  /**
   * BT2-FZ/BT3-TQ-FZ (R3): Tastatur für die Bezeichnung. Der Ziffernblock wird nur ausgeblendet
   * (Fenster und `cent` bleiben); „Fertig“ bringt ihn zurück und setzt den Fokus auf den Betrag.
   */
  const anzeige = useRef<HTMLDivElement | null>(null);
  const bt = useBildschirmtastatur({ standardZiel: 'bezeichnung' });
  /** MB2: laufende Direkteingabe der Stückzahl (null = Ziffernblock tippt den Betrag). */
  const [mengenEingabe, setMengenEingabe] = useState<MengenEingabe | null>(null);
  const wertKnopf = useRef<HTMLButtonElement | null>(null);

  /** Ziffernblock- bzw. Tastaturtaste: während der Direkteingabe auf die Menge, sonst Betrag. */
  function taste(t: ZiffernTaste): void {
    if (mengenEingabe !== null) {
      setMengenEingabe(eingabeTaste(mengenEingabe, t));
    } else {
      setCent((c) => ziffernTaste(c, t));
    }
  }

  /** M3/M6: Direkteingabe starten; eine offene Bildschirmtastatur schließt dabei (wie „Fertig“). */
  function eingabeOeffnen(): void {
    if (bt.tastatur !== null) {
      bt.schliessen('keiner');
    }
    setMengenEingabe(eingabeStarten(menge));
    wertKnopf.current?.focus();
  }

  /** M4: übernehmen (nur gültige Eingabe) bzw. verwerfen; die alte Menge bleibt sonst. */
  function eingabeBeenden(uebernehmen: boolean): void {
    if (mengenEingabe === null) {
      return;
    }
    if (uebernehmen) {
      setMenge(eingabeUebernehmen(mengenEingabe));
    }
    setMengenEingabe(null);
  }

  /** Stepper: eine laufende gültige Eingabe zählt als Ausgangswert, dann ±1. */
  function schritt(f: (m: number) => number): void {
    const basis =
      mengenEingabe !== null && eingabeGueltig(mengenEingabe)
        ? eingabeUebernehmen(mengenEingabe)
        : menge;
    setMengenEingabe(null);
    setMenge(f(basis));
  }

  function hinzufuegen(): void {
    const position = freiePosition({ cent, menge, steuersatz, bezeichnung });
    if (position === null) {
      return;
    }
    props.onHinzufuegen(position);
    setCent(() => 0);
    setMenge(1);
    setBezeichnung('');
    setMengenEingabe(null);
  }

  function bestaetigen(): void {
    if (mengenEingabe !== null) {
      if (eingabeGueltig(mengenEingabe)) {
        eingabeBeenden(true);
      }
    } else if (modus === 'betrag') {
      hinzufuegen();
    } else if (stueck !== null) {
      props.onVormerken?.(stueck);
    }
  }

  return (
    <div
      className={`ziffernblock menge-betrag-fenster${bt.tastatur === null ? '' : ' mit-tastatur'}`}
      tabIndex={0}
      onKeyDown={(e) => {
        // M5: Direkteingabe – Ziffern/⌫/Entf auf die Menge, Enter übernimmt, Esc nur zurück
        // zum Betrag; Komma, Punkt, Minus und Buchstaben bleiben wirkungslos (nur ganze Stück)
        if (mengenEingabe !== null && !(e.target instanceof HTMLInputElement)) {
          const m = tastaturZuMengenTaste(e.key);
          if (m === null) {
            return;
          }
          if (
            m.art === 'uebernehmen' &&
            e.target instanceof HTMLButtonElement &&
            e.target !== wertKnopf.current
          ) {
            return; // Enter auf einer anderen Taste löst nur diese Taste aus
          }
          e.preventDefault();
          e.stopPropagation();
          if (m.art === 'taste') {
            taste(m.taste);
          } else if (m.art === 'uebernehmen') {
            bestaetigen();
          } else {
            eingabeBeenden(false);
          }
          return;
        }
        if (e.key === 'Enter') {
          if (e.target instanceof HTMLButtonElement) {
            return; // Enter auf einer Taste löst nur diese Taste aus
          }
          e.preventDefault();
          bestaetigen();
          return;
        }
        // R5: erstes Esc bei offener Tastatur schließt nur sie
        if (e.key === 'Escape' && props.onAbbrechen !== undefined && !istTastaturEsc(e)) {
          e.preventDefault();
          props.onAbbrechen();
          return;
        }
        const t = tastaturZuZiffer(e.key);
        if (t !== null && !(e.target instanceof HTMLInputElement)) {
          e.preventDefault();
          taste(t);
        }
      }}
    >
      <div className="dlg-kopf">
        <h2 id="menge-betrag-titel">Menge oder Betrag</h2>
        <span className="dlg-meta">Tastatur und Ziffernblock</span>
      </div>
      <div className="chips" role="group" aria-label="Eingabe für">
        <button
          type="button"
          className={modus === 'betrag' ? 'aktiv' : ''}
          aria-pressed={modus === 'betrag'}
          onClick={() => {
            setModus('betrag');
          }}
        >
          Betrag
        </button>
        <button
          type="button"
          className={modus === 'stueck' ? 'aktiv' : ''}
          aria-pressed={modus === 'stueck'}
          onClick={() => {
            // R3: Tastatur zu und Ziel entfernen; der Betrag (cent) bleibt
            bt.schliessen('keiner');
            setMengenEingabe(null);
            setModus('stueck');
          }}
        >
          Stückzahl für nächste Kachel
        </button>
      </div>
      <div className="ziffern-haupt">
        <span className="dlg-feldname">
          {mengenEingabe !== null
            ? `Betrag ${formatiereEuro(cent)} € bleibt – der Ziffernblock tippt gerade die Menge`
            : modus === 'betrag'
              ? 'Betrag (Eingabe von rechts: 4 5 0 → 4,50 €)'
              : `Stückzahl (1–${String(MAX_VORGABE_MENGE)}) – gilt für die nächste Kachel oder den nächsten Scan`}
        </span>
        <div
          ref={anzeige}
          className={`ziffern-anzeige${mengenEingabe === null ? '' : ' ruhend'}`}
          aria-live="polite"
          tabIndex={-1}
          {...(mengenEingabe === null
            ? {}
            : {
                // M4: Antippen des Betrags kehrt ohne Mengenänderung zum Betrag zurück
                onClick: () => {
                  eingabeBeenden(false);
                  anzeige.current?.focus();
                },
              })}
          aria-label={modus === 'betrag' ? 'Betrag' : 'Stückzahl'}
        >
          {modus === 'betrag' ? `${formatiereEuro(cent)} €` : `${String(cent)}×`}
        </div>
        {modus === 'stueck' && cent > MAX_VORGABE_MENGE && (
          <p className="warnung">Höchstens {MAX_VORGABE_MENGE} Stück</p>
        )}
        {bt.tastatur === null && (
          <ZiffernTasten onTaste={taste} fokusHalten={mengenEingabe !== null} />
        )}
      </div>
      {modus === 'betrag' && (
        <div className="ziffern-seite">
          <span className="dlg-feldname">Steuersatz (nur für Betrag)</span>
          <div className="chips" role="group" aria-label="Steuersatz">
            {STEUERSAETZE.map((s) => (
              <button
                key={s.satz}
                type="button"
                className={s.satz === steuersatz ? 'aktiv' : ''}
                onClick={() => {
                  setSteuersatz(s.satz);
                }}
              >
                {s.satz} %
              </button>
            ))}
          </div>
          <span className="dlg-feldname">Bezeichnung (sonst „Artikel“)</span>
          <div className="ziffern-bezeichnung">
            <input
              value={bezeichnung}
              onChange={(e) => {
                setBezeichnung(e.target.value);
              }}
              placeholder="Bezeichnung (sonst „Artikel“)"
              aria-label="Bezeichnung"
              {...bt.feld('bezeichnung', {
                name: 'Bezeichnung',
                setzen: setBezeichnung,
                nachSchliessen: () => {
                  anzeige.current?.focus();
                },
              })}
            />
            <TastaturKnopf
              steuerung={bt.knopf('bezeichnung')}
              klein
              beschriftung="Tastatur für die Bezeichnung"
            />
            <Mengenfeld
              menge={menge}
              eingabe={mengenEingabe}
              wertKnopf={wertKnopf}
              onVerringern={() => {
                schritt(mengeVerringern);
              }}
              onErhoehen={() => {
                schritt(mengeErhoehen);
              }}
              onWert={() => {
                if (mengenEingabe === null) {
                  eingabeOeffnen();
                } else if (eingabeGueltig(mengenEingabe)) {
                  eingabeBeenden(true);
                }
              }}
            />
          </div>
        </div>
      )}
      {bt.tastatur !== null && (
        <Bildschirmtastatur steuerung={bt.tastatur} klasse="ziffern-tastatur" />
      )}
      <div className="dlg-fuss">
        {(props.onAbbrechen !== undefined || mengenEingabe !== null) && (
          <button
            type="button"
            className="taste zweit"
            data-kuerzel="Esc"
            onClick={() => {
              // M4: während der Direkteingabe bricht „Abbrechen“ nur die Eingabe ab
              if (mengenEingabe !== null) {
                eingabeBeenden(false);
              } else {
                props.onAbbrechen?.();
              }
            }}
          >
            Abbrechen
          </button>
        )}
        {mengenEingabe !== null ? (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={!eingabeGueltig(mengenEingabe)}
            onClick={() => {
              eingabeBeenden(true);
            }}
          >
            Übernehmen
          </button>
        ) : modus === 'betrag' ? (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={cent <= 0}
            onClick={hinzufuegen}
          >
            Hinzufügen
          </button>
        ) : (
          <button
            type="button"
            className="taste gross"
            data-kuerzel="Enter"
            disabled={stueck === null}
            onClick={bestaetigen}
          >
            Für nächste Kachel
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * MB2-Mengenfeld der freien Position (Komponente `MB2/Mengenfeld`, Zustände Normal,
 * Direkteingabe, Untergrenze, Obergrenze, Ungültig): [−] und [+] je 52 px, der Wert ist
 * antippbar und startet bzw. übernimmt die Direkteingabe.
 */
function Mengenfeld(props: {
  menge: number;
  eingabe: MengenEingabe | null;
  wertKnopf: { current: HTMLButtonElement | null };
  onVerringern: () => void;
  onErhoehen: () => void;
  onWert: () => void;
}) {
  const { menge, eingabe } = props;
  const ungueltig = eingabe !== null && !eingabeGueltig(eingabe);
  const zustand = eingabe === null ? '' : ungueltig ? ' eingabe ungueltig' : ' eingabe';
  return (
    <div className="mengenfeld" role="group" aria-label="Menge">
      <span className="dlg-feldname" aria-hidden="true">
        {eingabe === null
          ? 'Menge (Stück)'
          : ungueltig
            ? `Menge 1–${String(MENGE_MAX)} eingeben`
            : 'Menge – Ziffernblock tippt hier'}
      </span>
      <div className={`mengenfeld-stepper${zustand}`}>
        <button
          type="button"
          aria-label="Menge verringern"
          disabled={eingabe === null && !kannVerringern(menge)}
          onClick={props.onVerringern}
        >
          −
        </button>
        <button
          ref={(el) => {
            props.wertKnopf.current = el;
          }}
          type="button"
          className="mengenfeld-wert"
          aria-pressed={eingabe !== null}
          aria-label={
            eingabe === null
              ? `Menge ${String(menge)} – antippen zum Eingeben`
              : `Menge eingeben: ${eingabe.text === '' ? 'leer' : eingabe.text}`
          }
          onClick={props.onWert}
        >
          <span className="mengenfeld-zahl">
            {eingabe === null ? (
              <>
                {menge}
                <span className="mengenfeld-mal">×</span>
              </>
            ) : (
              <>
                {eingabe.text}
                <span className="mengenfeld-cursor" aria-hidden="true" />
              </>
            )}
          </span>
          <span className="mengenfeld-hinweis" aria-hidden="true">
            {eingabe !== null
              ? `1–${String(MENGE_MAX)}`
              : kannErhoehen(menge)
                ? 'antippen'
                : 'höchstens'}
          </span>
        </button>
        <button
          type="button"
          aria-label="Menge erhöhen"
          disabled={eingabe === null && !kannErhoehen(menge)}
          onClick={props.onErhoehen}
        >
          +
        </button>
      </div>
    </div>
  );
}
